import { uuid } from '@/littledash';
import {
  CAGE_NUMBER,
  chunkGroups,
  determineNextCageGroupName,
  getHighestCageGroupName,
  GROUP_NAME,
  GROUP_NUMBER,
  groupAndSortAnimals,
} from '../Recaging.utils';
import type { ProposedCage, RecagingAnimal } from '../Recaging.utils';

/**
 * Interface for study cage
 */
interface StudyCage {
  name: string;
}

/**
 * Custom hook for cage generation logic
 */
export const useCageGeneration = () => {
  const recage = (
    selectedSubjects: RecagingAnimal[],
    perCage: number,
    cageNamingMethod: string,
    nextCage: number | undefined,
    studyCages: StudyCage[],
    existingGroupCageNames: boolean,
    setExistingGroupCageNames: (value: boolean) => void,
    setProposedCages: (cages: ProposedCage[]) => void
  ) => {
    const cages: ProposedCage[] = [];
    const groupedAndSortedCages = groupAndSortAnimals(selectedSubjects);
    setExistingGroupCageNames(false);

    if (cageNamingMethod === CAGE_NUMBER) {
      let cageNumber = nextCage || 1;
      groupedAndSortedCages.forEach(({ group, animals }) => {
        chunkGroups(animals, perCage).forEach((chunk) => {
          cages.push({
            id: uuid(),
            cage: `Cage ${cageNumber}`,
            treatmentGroup: group,
            animals: chunk,
          });
          cageNumber++;
        });
      });
    }

    if (cageNamingMethod === GROUP_NUMBER) {
      groupedAndSortedCages.forEach(({ group, animals }) => {
        let highestGroupCageName = getHighestCageGroupName(
          group.no,
          Array.isArray(studyCages) ? studyCages.map((cage: StudyCage) => cage.name) : []
        );
        if (highestGroupCageName && !existingGroupCageNames) {
          setExistingGroupCageNames(true);
        }

        chunkGroups(animals, perCage).forEach((chunk) => {
          highestGroupCageName = determineNextCageGroupName(highestGroupCageName);
          cages.push({
            id: uuid(),
            cage:
              group.no != null && group.name !== 'Unassigned'
                ? `${group.no}${highestGroupCageName}`
                : `Unassigned ${highestGroupCageName}`,
            treatmentGroup: group,
            animals: chunk,
          });
        });
      });
    }

    if (cageNamingMethod === GROUP_NAME) {
      groupedAndSortedCages.forEach(({ group, animals }) => {
        const chunks = chunkGroups(animals, perCage);
        const isSingleCage = chunks.length === 1;

        if (isSingleCage) {
          // 1 cage per group should name the cage directly after the group name
          cages.push({
            id: uuid(),
            cage: group.name !== 'Unassigned' ? group.name : 'Unassigned',
            treatmentGroup: group,
            animals: chunks[0],
          });
        } else {
          // >1 cage per group should use suffixes
          let highestGroupCageName = getHighestCageGroupName(
            group.name,
            Array.isArray(studyCages) ? studyCages.map((cage: StudyCage) => cage.name) : []
          );

          if (highestGroupCageName && !existingGroupCageNames) {
            setExistingGroupCageNames(true);
          }

          chunks.forEach((chunk) => {
            highestGroupCageName = determineNextCageGroupName(highestGroupCageName);
            cages.push({
              id: uuid(),
              cage:
                group.name != null && group.name !== 'Unassigned'
                  ? `${group.name}_${highestGroupCageName}`
                  : `Unassigned ${highestGroupCageName}`,
              treatmentGroup: group,
              animals: chunk,
            });
          });
        }
      });
    }

    setProposedCages(cages);
  };

  return {
    recage,
  };
};
